import React, { useState, useEffect } from "react";
import Layout from "../Component/Layout";
import Select from "react-select";
import axios from "axios";
import { MASTER_API, PURCHASE_API } from "../apiConfig";
// Backend for Lab Equipment (LabEquipment_controller/_service/_routes.js) and
// Calibration Records (LabCalibrationRecord_controller/_service.js) are both
// real APIs now — see the routes file for the full endpoint list.
// The "Standard" picker talks to the existing Lab Calibration Standard API.

// ---------------------------------------------------------------------------
// Shared styling — copied to match Workcenter.js exactly so this tile feels
// like part of the same app.
// ---------------------------------------------------------------------------
const compactInput = {
  width: "100%",
  fontSize: "13px",
  padding: "4px 8px",
  height: "32px",
  boxSizing: "border-box",
  border: "1px solid #dee2e6",
  borderRadius: "4px",
};

const compactTextarea = {
  ...compactInput,
  height: "72px",
  padding: "8px",
  resize: "vertical",
};

const compactLabel = {
  display: "block",
  fontSize: "12px",
  fontWeight: 600,
  marginBottom: 2,
  color: "#334155",
};

const thStyle = {
  background: "#f8fafc",
  fontSize: 12,
  fontWeight: 600,
  padding: "10px",
  borderBottom: "1px solid #e5e7eb",
  color: "#1e293b",
  textAlign: "left",
  whiteSpace: "normal",
  wordBreak: "break-word",
};

const tdStyle = {
  fontSize: 13,
  padding: "10px",
  borderBottom: "1px solid #f1f5f9",
  color: "#334155",
  whiteSpace: "normal",
  wordBreak: "break-word",
};

const gridTwoCols = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
};

const gridThreeCols = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: 12,
};

const gridFourCols = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr 1fr",
  gap: 12,
};

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    borderRadius: "4px",
    borderColor: "#dee2e6",
    fontSize: "13px",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px", height: "32px" }),
  indicatorsContainer: (base) => ({ ...base, height: "32px" }),
  menu: (base) => ({ ...base, fontSize: "13px", zIndex: 20 }),
};

// eslint-disable-next-line react/prop-types
const TabButton = ({ index, activeTab, setActiveTab, label }) => (
  <button
    type="button"
    className={`btn btn-sm ${activeTab === index ? "btn-dark" : "btn-light"}`}
    onClick={() => setActiveTab(index)}
    style={{ fontSize: "13px", padding: "5px 12px", border: "1px solid #eee", whiteSpace: "nowrap", flexShrink: 0 }}
    aria-pressed={activeTab === index}
  >
    {label}
  </button>
);

const STATUS_OPTIONS = ["Calibrated", "Due Soon", "Overdue", "Out of Service"];

// The app stores the logged-in user under localStorage "user" as JSON
// ({ email, role, token, ... }) — there's no separate name field, so use
// the part of the email before "@" as a stand-in first name (e.g.
// "admin" from "admin@example.com").
const getLoggedInUserName = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("user") || "null");
    const email = stored?.email || "";
    return email.split("@")[0] || null;
  } catch (err) {
    return null;
  }
};

const statusColors = {
  "Calibrated": { bg: "#dcfce7", color: "#166534" },
  "Due Soon": { bg: "#fef9c3", color: "#854d0e" },
  "Overdue": { bg: "#fee2e2", color: "#991b1b" },
  "Out of Service": { bg: "#e2e8f0", color: "#475569" },
  "Pass": { bg: "#dcfce7", color: "#166534" },
  "Fail": { bg: "#fee2e2", color: "#991b1b" },
};

const StatusBadge = ({ status }) => {
  const c = statusColors[status] || statusColors["Calibrated"];
  return (
    <span
      style={{
        background: c.bg,
        color: c.color,
        fontSize: 11,
        fontWeight: 600,
        padding: "3px 8px",
        borderRadius: 999,
        whiteSpace: "nowrap",
      }}
    >
      {status || "-"}
    </span>
  );
};

const initialFormState = {
  // Header fields
  equipment_id: "",
  equipment_name: "",
  category: "",
  location: "",
  make: "",
  model_no: "",
  serial_no: "",
  measurement_range: "",
  least_count_accuracy: "",
  year_of_purchase: "",
  calibration_status: "Calibrated",
  first_calibration_date: "",
  last_calibration_date: "",
  next_due_date: "",

  // Calibration Schedule
  calibration_frequency_value: "",
  calibration_frequency_unit: "Months", // Months | Usage-based | Risk-based

  // Calibration Standards
  calibration_standard_id: null,
  calibration_standard_name: "",
  standard_parameters: [], // pulled in from the selected Standard (LabCalibrationStandard)

  // NOTE: Calibration Records are no longer flat fields on the equipment —
  // they live in their own calibration_records / calibration_record_details
  // tables, each with an auto-generated Record No. See calibrationRecords /
  // selectedRecord / newRecordParams state below.
};

const LabEquipment = () => {
  const [equipmentList, setEquipmentList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [message, setMessage] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [searchName, setSearchName] = useState("");
  const [searchId, setSearchId] = useState("");
  const [searchStatus, setSearchStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Standards dropdown — sourced from Configuration (calibration-standard master),
  // same source LabCalibrationStandard.js uses.
  const [standardOptions, setStandardOptions] = useState([]);
  const [loadingStandardParams, setLoadingStandardParams] = useState(false);

  // Category dropdown — sourced from Configuration (lab_category master),
  // managed under Configuration > Master Data > Lab Category.
  const [categoryOptions, setCategoryOptions] = useState([]);

  // Calibration Records tab:
  //  - calibrationRecords: past records for this equipment, shown as a list
  //    of Record No. hyperlinks.
  //  - selectedRecord: the record currently opened (by clicking a hyperlink)
  //    for viewing/editing its Actual Values; null = none open.
  //  - newRecordParams: a fresh, always-visible parameter grid (seeded from
  //    the selected Standard) for entering a brand-new record — Save here
  //    always creates a new record, it never overwrites an existing one.
  const [calibrationRecords, setCalibrationRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);

  const [selectedRecord, setSelectedRecord] = useState(null);
  const [loadingSelectedRecord, setLoadingSelectedRecord] = useState(false);
  const [savingSelectedRecord, setSavingSelectedRecord] = useState(false);

  const [newRecordParams, setNewRecordParams] = useState([]);
  const [newRecordCalibratedOn, setNewRecordCalibratedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [newRecordAgency, setNewRecordAgency] = useState("");
  const [newRecordCertNo, setNewRecordCertNo] = useState("");
  const [savingNewRecord, setSavingNewRecord] = useState(false);
  const [showNewRecordForm, setShowNewRecordForm] = useState(false);

  const fetchEquipment = async () => {
    try {
      const res = await axios.get(`${PURCHASE_API}/lab-equipment`);
      setEquipmentList(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const fetchStandardOptions = async () => {
      try {
        const res = await axios.get(
          `${MASTER_API}/configuration/dynamic/calibration-standard`
        );
        setStandardOptions(
          (res.data || []).map((s) => ({ value: s.id, label: s.name }))
        );
      } catch (err) {
        console.error(err);
      }
    };
    const fetchCategoryOptions = async () => {
      try {
        const res = await axios.get(`${MASTER_API}/configuration/dynamic/lab-category`);
        setCategoryOptions(
          (res.data || []).map((c) => ({ value: c.name, label: c.name }))
        );
      } catch (err) {
        console.error(err);
      }
    };
    fetchEquipment();
    fetchStandardOptions();
    fetchCategoryOptions();
    // eslint-disable-next-line
  }, []);

  const updateForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Recompute First / Last / Next Calibration Date live in the form,
  // any time the frequency, its unit, or the fetched calibration records
  // change — no need to save first.
  //   First Calibration Date = earliest calibration record's raw date
  //   Last Calibration Date  = latest calibration record's raw date
  //   Next Calibration Date  = Last Calibration Date + frequency
  useEffect(() => {
    if (!showModal) return;

    let firstRecordDate = null;
    let latestRecordDate = null;
    calibrationRecords.forEach((r) => {
      if (!firstRecordDate || new Date(r.created_at) < new Date(firstRecordDate)) firstRecordDate = r.created_at;
      if (!latestRecordDate || new Date(r.created_at) > new Date(latestRecordDate)) latestRecordDate = r.created_at;
    });

    const first = firstRecordDate ? new Date(firstRecordDate) : null;
    const last = latestRecordDate ? new Date(latestRecordDate) : null;
    const next = addFrequency(last, form.calibration_frequency_value, form.calibration_frequency_unit);

    setForm((f) => ({
      ...f,
      first_calibration_date: first ? toDateInputValue(first.toISOString()) : "",
      last_calibration_date: last ? toDateInputValue(last.toISOString()) : "",
      next_due_date: next ? toDateInputValue(next.toISOString()) : "",
    }));
    // eslint-disable-next-line
  }, [form.calibration_frequency_value, form.calibration_frequency_unit, calibrationRecords, showModal]);

  // When a Standard is picked, pull in all of its parameters
  // (Parameter / Verification Parameter / UOM / Specified Value) so they
  // show up under the Calibration Standards tab.
  const handleStandardSelect = async (opt) => {
    if (!opt) {
      updateForm({ calibration_standard_id: null, calibration_standard_name: "", standard_parameters: [] });
      setNewRecordParams([]);
      return;
    }
    updateForm({ calibration_standard_id: opt.value, calibration_standard_name: opt.label });
    setLoadingStandardParams(true);
    try {
      const res = await axios.get(`${PURCHASE_API}/lab-calibration-standard/${opt.value}`);
      const params = res.data?.parameters || [];
      updateForm({ standard_parameters: params });
      // Picking a (new) standard reseeds the "new record" entry grid — it
      // doesn't touch any already-saved records in the list below.
      setNewRecordParams(
        params.map((p) => ({
          sl_no: p.sl_no,
          parameter_name: p.parameter_name,
          verification_parameter: p.verification_parameter,
          uom: p.uom,
          specified_value: p.specified_value,
          actual_value: "",
        }))
      );
    } catch (err) {
      console.error(err);
      updateForm({ standard_parameters: [] });
      setNewRecordParams([]);
    } finally {
      setLoadingStandardParams(false);
    }
  };

  // -------------------------------------------------------------------
  // Calibration Records (own tables: calibration_records + _details)
  // -------------------------------------------------------------------
  const fetchCalibrationRecords = async (equipmentId) => {
    if (!equipmentId) return;
    setLoadingRecords(true);
    try {
      const res = await axios.get(`${PURCHASE_API}/calibration-records/equipment/${equipmentId}`);
      setCalibrationRecords(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingRecords(false);
    }
  };

  // Clicking a Record No. link — loads that record's saved parameters/actual
  // values so they can be reviewed or edited in place.
  const openExistingCalibrationRecord = async (id) => {
    setLoadingSelectedRecord(true);
    try {
      const res = await axios.get(`${PURCHASE_API}/calibration-records/${id}`);
      setSelectedRecord(res.data);
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Failed to load calibration record." });
    } finally {
      setLoadingSelectedRecord(false);
    }
  };

  const closeSelectedRecord = () => setSelectedRecord(null);

  const updateSelectedRecordParam = (index, value) => {
    setSelectedRecord((r) => {
      const parameters = [...r.parameters];
      parameters[index] = { ...parameters[index], actual_value: value };
      return { ...r, parameters };
    });
  };

  // Saves edits to the record currently open above the list — updates that
  // one record, doesn't create a new one.
  const saveSelectedRecord = async () => {
    setSavingSelectedRecord(true);
    try {
      await axios.put(`${PURCHASE_API}/calibration-records/${selectedRecord.id}`, {
        parameters: selectedRecord.parameters,
        calibrated_on: selectedRecord.created_at ? String(selectedRecord.created_at).slice(0, 10) : undefined,
        calibration_agency: selectedRecord.calibration_agency,
        certificate_no: selectedRecord.certificate_no,
      });
      setMessage({ type: "success", text: "Calibration record updated." });
      setSelectedRecord(null);
      fetchCalibrationRecords(editingId);
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: err?.response?.data?.error || "Failed to update calibration record." });
    } finally {
      setSavingSelectedRecord(false);
    }
  };

  const updateNewRecordParam = (index, value) => {
    setNewRecordParams((params) => {
      const next = [...params];
      next[index] = { ...next[index], actual_value: value };
      return next;
    });
  };

  // Saving here always creates a brand-new record — the backend generates
  // its CR-prefixed Record No. and snapshots this parameter grid with its
  // Actual Values. If the equipment itself hasn't been saved yet (brand-new,
  // no id), it's saved first so the calibration record has a real
  // equipment_id to attach to (FK) — no separate "save equipment first"
  // step needed. After a successful save the grid resets to blank Actual
  // Values so another new record can be entered right away.
  const saveNewRecord = async () => {
    if (!form.equipment_id || !form.equipment_name) {
      setMessage({ type: "error", text: "Equipment ID and Equipment Name are required." });
      setActiveTab(0);
      return;
    }
    if (!form.calibration_standard_id) {
      setMessage({ type: "error", text: "Select a Standard first, so its parameters can be recorded." });
      setActiveTab(1);
      return;
    }

    setSavingNewRecord(true);
    try {
      let currentEquipmentId = editingId;

      if (!currentEquipmentId) {
        const eqRes = await axios.post(`${PURCHASE_API}/lab-equipment`, form);
        currentEquipmentId = eqRes.data?.data?.id;
        setEditingId(currentEquipmentId);
        fetchEquipment();
      }

      await axios.post(`${PURCHASE_API}/calibration-records`, {
        equipment_id: currentEquipmentId,
        calibration_standard_id: form.calibration_standard_id,
        calibration_standard_name: form.calibration_standard_name,
        calibrated_on: newRecordCalibratedOn,
        calibration_agency: newRecordAgency,
        certificate_no: newRecordCertNo,
        // TODO: swap for however the app identifies the logged-in user
        // elsewhere (see the created_by pattern used in Goodsreceipt.js).
        created_by: getLoggedInUserName(),
        parameters: newRecordParams,
      });

      setMessage({ type: "success", text: "Calibration record saved." });
      // Reset the entry grid (keep the same parameter rows, clear values)
      // so a follow-up record can be started immediately, and collapse it
      // back behind the "+ Start Calibration" button.
      setNewRecordParams((params) => params.map((p) => ({ ...p, actual_value: "" })));
      setNewRecordCalibratedOn(new Date().toISOString().slice(0, 10));
      setNewRecordAgency("");
      setNewRecordCertNo("");
      setShowNewRecordForm(false);
      fetchCalibrationRecords(currentEquipmentId);
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: err?.response?.data?.error || "Failed to save calibration record." });
    } finally {
      setSavingNewRecord(false);
    }
  };

  // -------------------------------------------------------------------
  // Filtering / pagination
  // -------------------------------------------------------------------
  const filteredEquipment = equipmentList.filter((eq) => {
    const matchName =
      !searchName ||
      eq.equipment_name?.toLowerCase().includes(searchName.toLowerCase());
    const matchId =
      !searchId || eq.equipment_id?.toLowerCase().includes(searchId.toLowerCase());
    const matchStatus = !searchStatus || eq.calibration_status === searchStatus;
    return matchName && matchId && matchStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filteredEquipment.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEquipment = filteredEquipment.slice(startIndex, startIndex + itemsPerPage);

  // -------------------------------------------------------------------
  // Modal open / close
  // -------------------------------------------------------------------
  // Backend returns full datetime strings for DATE columns sometimes;
  // <input type="date"> only accepts YYYY-MM-DD.
  const toDateInputValue = (v) => (v ? String(v).slice(0, 10) : "");

  // Mirrors LabEquipment.service.js's addFrequency() so the three
  // calibration dates can be shown live in the form, before saving —
  // only time-based units get date math; Usage-based/Risk-based -> null.
  const addFrequency = (baseDate, value, unit) => {
    if (!baseDate || !value) return null;
    const d = new Date(baseDate);
    if (Number.isNaN(d.getTime())) return null;
    const n = Number(value);
    if (Number.isNaN(n)) return null;

    switch ((unit || "").toLowerCase()) {
      case "months":
        d.setMonth(d.getMonth() + n);
        return d;
      case "years":
        d.setFullYear(d.getFullYear() + n);
        return d;
      case "weeks":
        d.setDate(d.getDate() + n * 7);
        return d;
      case "days":
        d.setDate(d.getDate() + n);
        return d;
      default:
        return null;
    }
  };

  const openCreateModal = () => {
    setEditingId(null);
    setForm({ ...initialFormState, created_at: new Date().toISOString() });
    setActiveTab(0);
    setShowModal(true);
    setMessage(null);
    setCalibrationRecords([]);
    setSelectedRecord(null);
    setNewRecordParams([]);
  };

  const openEditModal = async (eq) => {
    setEditingId(eq.id);
    setForm({
      ...initialFormState,
      ...eq,
      first_calibration_date: toDateInputValue(eq.first_calibration_date),
      last_calibration_date: toDateInputValue(eq.last_calibration_date),
      next_due_date: toDateInputValue(eq.next_due_date),
      standard_parameters: [],
    });
    setActiveTab(0);
    setShowModal(true);
    setMessage(null);
    setCalibrationRecords([]);
    setSelectedRecord(null);
    setNewRecordParams([]);
    setNewRecordCalibratedOn(new Date().toISOString().slice(0, 10));
    setShowNewRecordForm(false);

    // Re-pull the standard's parameters so the Calibration Standards tab
    // (and the "new record" Actual Value grid) shows the current parameter
    // list, not a stale snapshot.
    if (eq.calibration_standard_id) {
      setLoadingStandardParams(true);
      try {
        const res = await axios.get(
          `${PURCHASE_API}/lab-calibration-standard/${eq.calibration_standard_id}`
        );
        const standardParams = res.data?.parameters || [];
        updateForm({ standard_parameters: standardParams });
        setNewRecordParams(
          standardParams.map((p) => ({
            sl_no: p.sl_no,
            parameter_name: p.parameter_name,
            verification_parameter: p.verification_parameter,
            uom: p.uom,
            specified_value: p.specified_value,
            actual_value: "",
          }))
        );
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingStandardParams(false);
      }
    }

    // Past records for this equipment show up as Record No. hyperlinks.
    fetchCalibrationRecords(eq.id);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setMessage(null);
    setSelectedRecord(null);
  };

  const openDeleteConfirm = (eq) => setDeleteTarget(eq);
  const closeDeleteConfirm = () => setDeleteTarget(null);

  const confirmDeleteEquipment = async () => {
    try {
      await axios.delete(`${PURCHASE_API}/lab-equipment/${deleteTarget.id}`);
      setMessage({ type: "success", text: "Equipment deleted." });
      setDeleteTarget(null);
      fetchEquipment();
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Failed to delete equipment." });
      setDeleteTarget(null);
    }
  };

  // -------------------------------------------------------------------
  // Save (create or update)
  // -------------------------------------------------------------------
  const handleSave = async () => {
    if (!form.equipment_id || !form.equipment_name) {
      setMessage({ type: "error", text: "Equipment ID and Equipment Name are required." });
      setActiveTab(0);
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${PURCHASE_API}/lab-equipment/${editingId}`, form);
        setMessage({ type: "success", text: "Equipment updated." });
      } else {
        await axios.post(`${PURCHASE_API}/lab-equipment`, form);
        setMessage({ type: "success", text: "Equipment created." });
      }
      setShowModal(false);
      fetchEquipment();
    } catch (err) {
      console.error(err);
      setMessage({
        type: "error",
        text: err?.response?.data?.error || "Save failed. Please try again.",
      });
    }
  };

  return (
    <Layout>
      <div style={{ padding: 20 }}>
        {/* ===================== HEADER ===================== */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 26, fontWeight: 700, color: "#1e293b" }}>Lab Equipment</div>
            <div style={{ fontSize: 13, color: "#64748b" }}>Manage equipment calibration records</div>
          </div>
          <button
            className="btn btn-dark"
            onClick={openCreateModal}
            style={{ padding: "8px 16px", background: "#1e293b", color: "white", border: "none", borderRadius: 4, cursor: "pointer" }}
          >
            + Create Equipment
          </button>
        </div>

        {/* ===================== FILTERS ===================== */}
        <div style={{ background: "#ffffff", borderRadius: 8, padding: 15, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: 15 }}>
          <div className="row g-3">
            <div className="col-md-4">
              <label style={compactLabel}>Equipment ID</label>
              <input
                type="text"
                placeholder="Search Equipment ID"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                style={compactInput}
              />
            </div>
            <div className="col-md-4">
              <label style={compactLabel}>Equipment Name</label>
              <input
                type="text"
                placeholder="Search Equipment Name"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                style={compactInput}
              />
            </div>
            <div className="col-md-4">
              <label style={compactLabel}>Calibration Status</label>
              <select
                value={searchStatus}
                onChange={(e) => setSearchStatus(e.target.value)}
                style={compactInput}
              >
                <option value="">All Statuses</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {message && (
          <div
            style={{
              marginBottom: 16,
              padding: 10,
              background: message.type === "error" ? "#ffe6e6" : "#e8f7ea",
              color: message.type === "error" ? "#d63031" : "#1b5e20",
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            {message.text}
          </div>
        )}

        {/* ===================== DATA TABLE ===================== */}
        <div style={{ width: "100%", background: "white", borderRadius: 6, overflowX: "auto", fontSize: "13px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f1f1f1" }}>
                <th style={thStyle}>Equipment ID</th>
                <th style={thStyle}>Equipment Name</th>
                <th style={thStyle}>Lab Category</th>
                <th style={thStyle}>Calibration Status</th>
                <th style={thStyle}>Last Calibration</th>
                <th style={thStyle}>Next Due</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {equipmentList.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: 20, textAlign: "center", color: "#777" }}>
                    No Lab Equipment found
                  </td>
                </tr>
              ) : (
                paginatedEquipment.map((eq) => (
                  <tr key={eq.id}>
                    <td style={tdStyle}>{eq.equipment_id}</td>
                    <td style={tdStyle}>{eq.equipment_name}</td>
                    <td style={tdStyle}>{eq.category || "-"}</td>
                    <td style={tdStyle}><StatusBadge status={eq.calibration_status} /></td>
                    <td style={tdStyle}>{eq.last_calibration_date || "-"}</td>
                    <td style={tdStyle}>{eq.next_due_date || "-"}</td>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          onClick={() => openEditModal(eq)}
                          className="btn btn-outline-dark btn-sm"
                          style={{ fontSize: "11px", padding: "2px 8px" }}
                        >
                          View
                        </button>
                        <button
                          onClick={() => openDeleteConfirm(eq)}
                          title="Delete Equipment"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 28,
                            height: 28,
                            padding: 0,
                            border: "1px solid #fecaca",
                            borderRadius: 6,
                            background: "#fee2e2",
                            cursor: "pointer",
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6" />
                            <path d="M14 11v6" />
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginTop: 15 }}>
          <button className="btn btn-light" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>{"<"}</button>
          <span>Page {currentPage} of {totalPages}</span>
          <button className="btn btn-light" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>{">"}</button>
        </div>

        {/* ===================== CREATE / EDIT MODAL ===================== */}
        {showModal && (
          <>
            <div
              onClick={closeModal}
              style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 1050 }}
            />
            <div
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "95%",
                maxWidth: 900,
                maxHeight: "90vh",
                overflowY: "auto",
                overflowX: "hidden",
                background: "white",
                zIndex: 1055,
                borderRadius: 8,
                boxShadow: "0 0 20px rgba(0,0,0,0.3)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div style={{ padding: "12px 20px", borderBottom: "1px solid #ddd", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0, color: "#1e293b", fontSize: 18, fontWeight: 600 }}>
                  {editingId ? "Edit Lab Equipment" : "Create Lab Equipment"}
                </h3>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <button
                    onClick={handleSave}
                    style={{ padding: "7px 16px", background: "#1e293b", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 600, fontSize: 13 }}
                  >
                    Save
                  </button>
                  <button onClick={closeModal} style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 16, color: "#64748b" }}>✖</button>
                </div>
              </div>

              <div style={{ padding: "16px 20px", overflowY: "auto" }}>
                {/* Save/error feedback — rendered here (not just on the page
                    behind the overlay) so it's actually visible while the
                    modal is open. */}
                {message && (
                  <div
                    style={{
                      marginBottom: 14,
                      padding: 10,
                      background: message.type === "error" ? "#ffe6e6" : "#e8f7ea",
                      color: message.type === "error" ? "#d63031" : "#1b5e20",
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  >
                    {message.text}
                  </div>
                )}

                {/* Header fields, always visible regardless of tab */}
                <div style={gridFourCols}>
                  <div>
                    <label style={compactLabel}>Equipment ID *</label>
                    <input
                      type="text"
                      value={form.equipment_id || ""}
                      onChange={(e) => updateForm({ equipment_id: e.target.value })}
                      placeholder="e.g. EQ-0003"
                      style={compactInput}
                    />
                  </div>
                  <div>
                    <label style={compactLabel}>Equipment Name *</label>
                    <input
                      type="text"
                      value={form.equipment_name || ""}
                      onChange={(e) => updateForm({ equipment_name: e.target.value })}
                      placeholder="Enter Equipment Name"
                      style={compactInput}
                    />
                  </div>
                  <div>
                    <label style={compactLabel}>Lab Category</label>
                    <Select
                      styles={selectStyles}
                      options={categoryOptions}
                      value={
                        form.category
                          ? { value: form.category, label: form.category }
                          : null
                      }
                      onChange={(opt) => updateForm({ category: opt ? opt.value : "" })}
                      placeholder="Select a category..."
                      isClearable
                    />
                  </div>
                  <div>
                    <label style={compactLabel}>Standard</label>
                    <Select
                      styles={selectStyles}
                      options={standardOptions}
                      value={
                        form.calibration_standard_id
                          ? { value: form.calibration_standard_id, label: form.calibration_standard_name }
                          : null
                      }
                      onChange={handleStandardSelect}
                      placeholder="Select a standard..."
                      isClearable
                    />
                  </div>
                </div>

                {/* Equipment details */}
                <div style={{ ...gridFourCols, marginTop: 12 }}>
                  <div>
                    <label style={compactLabel}>Location</label>
                    <input
                      type="text"
                      value={form.location || ""}
                      onChange={(e) => updateForm({ location: e.target.value })}
                      placeholder="Enter Location"
                      style={compactInput}
                    />
                  </div>
                  <div>
                    <label style={compactLabel}>Make</label>
                    <input
                      type="text"
                      value={form.make || ""}
                      onChange={(e) => updateForm({ make: e.target.value })}
                      placeholder="Enter Make"
                      style={compactInput}
                    />
                  </div>
                  <div>
                    <label style={compactLabel}>Model No.</label>
                    <input
                      type="text"
                      value={form.model_no || ""}
                      onChange={(e) => updateForm({ model_no: e.target.value })}
                      placeholder="Enter Model No."
                      style={compactInput}
                    />
                  </div>
                  <div>
                    <label style={compactLabel}>Serial No. / Ref. No.</label>
                    <input
                      type="text"
                      value={form.serial_no || ""}
                      onChange={(e) => updateForm({ serial_no: e.target.value })}
                      placeholder="Enter Serial / Ref. No."
                      style={compactInput}
                    />
                  </div>
                  <div>
                    <label style={compactLabel}>Range</label>
                    <input
                      type="text"
                      value={form.measurement_range || ""}
                      onChange={(e) => updateForm({ measurement_range: e.target.value })}
                      placeholder="e.g. 0-150 mm"
                      style={compactInput}
                    />
                  </div>
                  <div>
                    <label style={compactLabel}>Least Count / Accuracy</label>
                    <input
                      type="text"
                      value={form.least_count_accuracy || ""}
                      onChange={(e) => updateForm({ least_count_accuracy: e.target.value })}
                      placeholder="e.g. 0.01 mm / ±0.02"
                      style={compactInput}
                    />
                  </div>
                  <div>
                    <label style={compactLabel}>Year of Purchase</label>
                    <input
                      type="text"
                      value={form.year_of_purchase || ""}
                      onChange={(e) => updateForm({ year_of_purchase: e.target.value })}
                      placeholder="e.g. 2024"
                      style={compactInput}
                    />
                  </div>
                </div>

                {/* Read-only audit fields, always visible in the header row too */}
                <div style={{ ...gridTwoCols, marginTop: 12 }}>
                  <div>
                    <label style={compactLabel}>Created On</label>
                    <input
                      type="text"
                      disabled
                      value={
                        editingId
                          ? (form.created_at ? new Date(form.created_at).toLocaleDateString() : "-")
                          : new Date().toLocaleDateString()
                      }
                      style={{ ...compactInput, background: "#f1f5f9", color: "#64748b" }}
                    />
                  </div>
                  <div>
                    <label style={compactLabel}>Changed On</label>
                    <input
                      type="text"
                      disabled
                      value={
                        editingId && form.updated_at
                          ? new Date(form.updated_at).toLocaleDateString()
                          : "-"
                      }
                      style={{ ...compactInput, background: "#f1f5f9", color: "#64748b" }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 5,
                    marginBottom: 15,
                    marginTop: 14,
                    flexWrap: "nowrap",
                    overflowX: "auto",
                    paddingBottom: 4,
                  }}
                >
                  <TabButton index={0} activeTab={activeTab} setActiveTab={setActiveTab} label="Calibration Schedule" />
                  <TabButton index={1} activeTab={activeTab} setActiveTab={setActiveTab} label="Calibration Standards" />
                  <TabButton index={2} activeTab={activeTab} setActiveTab={setActiveTab} label="Calibration Records" />
                </div>
                <hr style={{ margin: "10px 0" }} />

                <div style={{ marginTop: 6 }}>
                  {/* ---- Tab 0: Calibration Schedule ---- */}
                  {activeTab === 0 && (
                    <>
                      <div style={gridTwoCols}>
                        <div>
                          <label style={compactLabel}>Calibration Status</label>
                          <select
                            value={form.calibration_status || ""}
                            onChange={(e) => updateForm({ calibration_status: e.target.value })}
                            style={compactInput}
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={compactLabel}>First Calibration Date</label>
                          <input
                            type="date"
                            value={form.first_calibration_date || ""}
                            disabled
                            style={{ ...compactInput, background: "#f1f5f9", color: "#64748b" }}
                          />
                        </div>
                      </div>
                      <div style={{ ...gridTwoCols, marginTop: 12 }}>
                        <div>
                          <label style={compactLabel}>Last Calibration Date</label>
                          <input
                            type="date"
                            value={form.last_calibration_date || ""}
                            disabled
                            style={{ ...compactInput, background: "#f1f5f9", color: "#64748b" }}
                          />
                        </div>
                        <div>
                          <label style={compactLabel}>Next Calibration Date</label>
                          <input
                            type="date"
                            value={form.next_due_date || ""}
                            disabled
                            style={{ ...compactInput, background: "#f1f5f9", color: "#64748b" }}
                          />
                        </div>
                      </div>

                      <div style={{ ...gridTwoCols, marginTop: 12 }}>
                        <div>
                          <label style={compactLabel}>Calibration Frequency</label>
                          <input
                            type="number"
                            min="0"
                            value={form.calibration_frequency_value || ""}
                            onChange={(e) => updateForm({ calibration_frequency_value: e.target.value })}
                            placeholder="e.g. 6"
                            style={compactInput}
                          />
                        </div>
                        <div>
                          <label style={compactLabel}>UOM</label>
                          <select
                            value={form.calibration_frequency_unit || ""}
                            onChange={(e) => updateForm({ calibration_frequency_unit: e.target.value })}
                            style={compactInput}
                          >
                            <option value="Months">Months</option>
                            <option value="Days">Days</option>
                            <option value="years">Years</option>
                          </select>
                        </div>
                      </div>
                    </>
                  )}

                  {/* ---- Tab 1: Calibration Standards ---- */}
                  {activeTab === 1 && (
                    <>
                      <div style={{ marginTop: 4 }}>
                        <label style={compactLabel}></label>
                        {!form.calibration_standard_id && (
                          <div style={{ fontSize: 13, color: "#94a3b8", padding: "10px 0" }}>
                            Select a Standard above to load its parameters.
                          </div>
                        )}
                        {form.calibration_standard_id && loadingStandardParams && (
                          <div style={{ fontSize: 13, color: "#94a3b8", padding: "10px 0" }}>
                            Loading parameters…
                          </div>
                        )}
                        {form.calibration_standard_id && !loadingStandardParams && (
                          form.standard_parameters?.length ? (
                            <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, overflowX: "auto" }}>
                              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
                                <thead>
                                  <tr>
                                    <th style={thStyle}>Parameter</th>
                                    <th style={thStyle}>Verification Parameter</th>
                                    <th style={thStyle}>UOM</th>
                                    <th style={thStyle}>Specified Value</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {form.standard_parameters.map((p, i) => (
                                    <tr key={i}>
                                      <td style={tdStyle}>{p.parameter_name}</td>
                                      <td style={tdStyle}>{p.verification_parameter}</td>
                                      <td style={tdStyle}>{p.uom}</td>
                                      <td style={tdStyle}>{p.specified_value}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div style={{ fontSize: 13, color: "#94a3b8", padding: "10px 0" }}>
                              This standard has no parameters configured.
                            </div>
                          )
                        )}
                      </div>
                    </>
                  )}

                  {/* ---- Tab 2: Calibration Records ---- */}
                  {/* New-record entry grid stays at the top so it's the first thing
                      you see. Saved records list below it, with Record No. hyperlinks —
                      clicking one opens it for editing (updates that record) above the list. */}
                  {activeTab === 2 && (
                    <div>
                      {/* ---- Collapsed by default: a single "+ Start Calibration"
                          button. Clicking it reveals the entry grid below. ---- */}
                      {!showNewRecordForm && (
                        <div style={{ marginBottom: 20, display: "flex", justifyContent: "flex-end" }}>
                          <button
                            onClick={() => setShowNewRecordForm(true)}
                            style={{ padding: "8px 16px", background: "#1e293b", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 600, fontSize: 13 }}
                          >
                            + Start Calibration
                          </button>
                        </div>
                      )}

                      {showNewRecordForm && (
                        <>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                              New Calibration Record
                            </div>
                            <button
                              onClick={() => setShowNewRecordForm(false)}
                              style={{ background: "transparent", border: "none", color: "#64748b", cursor: "pointer", fontSize: 12 }}
                            >
                              ✕ Close
                            </button>
                          </div>

                          <div style={{ ...gridTwoCols, marginBottom: 12 }}>
                            <div>
                              <label style={compactLabel}>Calibration Agency</label>
                              <input type="text" value={newRecordAgency} onChange={(e) => setNewRecordAgency(e.target.value)} placeholder="Enter Calibration Agency" style={compactInput} />
                            </div>
                            <div>
                              <label style={compactLabel}>Certificate No.</label>
                              <input type="text" value={newRecordCertNo} onChange={(e) => setNewRecordCertNo(e.target.value)} placeholder="Enter Certificate No." style={compactInput} />
                            </div>
                          </div>

                      {!form.calibration_standard_id && (
                        <div style={{ fontSize: 13, color: "#94a3b8", padding: "10px 0" }}>
                          Select a Standard above to load its parameters here.
                        </div>
                      )}

                      {form.calibration_standard_id && loadingStandardParams && (
                        <div style={{ fontSize: 13, color: "#94a3b8", padding: "10px 0" }}>Loading…</div>
                      )}

                      {form.calibration_standard_id && !loadingStandardParams && (
                        newRecordParams.length ? (
                          <>
                            <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, overflowX: "auto" }}>
                              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 680 }}>
                                <thead>
                                  <tr>
                                    <th style={thStyle}>Parameter</th>
                                    <th style={thStyle}>Verification Parameter</th>
                                    <th style={thStyle}>UOM</th>
                                    <th style={thStyle}>Specified Value</th>
                                    <th style={{ ...thStyle, minWidth: 150 }}>Calibrated On</th>
                                    <th style={{ ...thStyle, minWidth: 140 }}>Actual Value</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {newRecordParams.map((p, i) => (
                                    <tr key={i}>
                                      <td style={tdStyle}>{p.parameter_name}</td>
                                      <td style={tdStyle}>{p.verification_parameter}</td>
                                      <td style={tdStyle}>{p.uom}</td>
                                      <td style={tdStyle}>{p.specified_value}</td>
                                      <td style={tdStyle}>
                                        {i === 0 ? (
                                          <input
                                            type="date"
                                            value={newRecordCalibratedOn}
                                            onChange={(e) => setNewRecordCalibratedOn(e.target.value)}
                                            style={{ ...compactInput, height: 28 }}
                                          />
                                        ) : null}
                                      </td>
                                      <td style={tdStyle}>
                                        <input
                                          type="text"
                                          value={p.actual_value || ""}
                                          onChange={(e) => updateNewRecordParam(i, e.target.value)}
                                          style={{ ...compactInput, height: 28 }}
                                        />
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                              <button
                                onClick={saveNewRecord}
                                disabled={savingNewRecord}
                                style={{ padding: "7px 16px", background: "#1e293b", color: "white", border: "none", borderRadius: 4, cursor: savingNewRecord ? "default" : "pointer", opacity: savingNewRecord ? 0.7 : 1, fontWeight: 600, fontSize: 13 }}
                              >
                                {savingNewRecord ? "Saving…" : "Save"}
                              </button>
                            </div>
                          </>
                        ) : (
                          <div style={{ fontSize: 13, color: "#94a3b8", padding: "10px 0" }}>
                            This standard has no parameters configured.
                          </div>
                        )
                      )}
                        </>
                      )}

                      {/* ---- Editing a record opened from the list below ---- */}
                      {loadingSelectedRecord && (
                        <div style={{ fontSize: 13, color: "#94a3b8", padding: "10px 0", marginTop: 18 }}>Loading…</div>
                      )}
                      {selectedRecord && !loadingSelectedRecord && (
                        <div style={{ marginTop: 22, marginBottom: 20, paddingTop: 18, paddingBottom: 18, borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", display: "flex", alignItems: "center", gap: 12 }}>
                              {selectedRecord.record_no}
                              <span>
                                <StatusBadge status="Calibrated" />
                              </span>
                            </div>
                            <button
                              onClick={closeSelectedRecord}
                              style={{ background: "transparent", border: "none", color: "#64748b", cursor: "pointer", fontSize: 12 }}
                            >
                              ✕ Close
                            </button>
                          </div>

                          <div style={{ ...gridTwoCols, marginBottom: 12 }}>
                            <div>
                              <label style={compactLabel}>Calibration Agency</label>
                              <input type="text" value={selectedRecord.calibration_agency || ""} onChange={(e) => setSelectedRecord((r) => ({ ...r, calibration_agency: e.target.value }))} style={compactInput} />
                            </div>
                            <div>
                              <label style={compactLabel}>Certificate No.</label>
                              <input type="text" value={selectedRecord.certificate_no || ""} onChange={(e) => setSelectedRecord((r) => ({ ...r, certificate_no: e.target.value }))} style={compactInput} />
                            </div>
                          </div>

                          <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 680 }}>
                              <thead>
                                <tr>
                                  <th style={thStyle}>Parameter</th>
                                  <th style={thStyle}>Verification Parameter</th>
                                  <th style={thStyle}>UOM</th>
                                  <th style={thStyle}>Specified Value</th>
                                  <th style={{ ...thStyle, minWidth: 150 }}>Calibrated On</th>
                                  <th style={{ ...thStyle, minWidth: 140 }}>Actual Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedRecord.parameters.map((p, i) => (
                                  <tr key={p.id || i}>
                                    <td style={tdStyle}>{p.parameter_name}</td>
                                    <td style={tdStyle}>{p.verification_parameter}</td>
                                    <td style={tdStyle}>{p.uom}</td>
                                    <td style={tdStyle}>{p.specified_value}</td>
                                    <td style={tdStyle}>
                                      {i === 0 ? (
                                        <input
                                          type="date"
                                          value={selectedRecord.created_at ? String(selectedRecord.created_at).slice(0, 10) : ""}
                                          onChange={(e) =>
                                            setSelectedRecord((r) => ({ ...r, created_at: e.target.value }))
                                          }
                                          style={{ ...compactInput, height: 28 }}
                                        />
                                      ) : null}
                                    </td>
                                    <td style={tdStyle}>
                                      <input
                                        type="text"
                                        value={p.actual_value || ""}
                                        onChange={(e) => updateSelectedRecordParam(i, e.target.value)}
                                        style={{ ...compactInput, height: 28 }}
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                            <button
                              onClick={saveSelectedRecord}
                              disabled={savingSelectedRecord}
                              style={{ padding: "7px 16px", background: "#1e293b", color: "white", border: "none", borderRadius: 4, cursor: savingSelectedRecord ? "default" : "pointer", opacity: savingSelectedRecord ? 0.7 : 1, fontWeight: 600, fontSize: 13 }}
                            >
                              {savingSelectedRecord ? "Saving…" : "Save"}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* ---- Past records list ---- */}
                      {loadingRecords ? (
                        <div style={{ fontSize: 13, color: "#94a3b8", padding: "10px 0", marginTop: 22 }}>Loading…</div>
                      ) : calibrationRecords.length > 0 ? (
                        <div style={{ marginTop: 22, border: "1px solid #e5e7eb", borderRadius: 6, overflowX: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr>
                                <th style={thStyle}>Record No.</th>
                                <th style={thStyle}>Status</th>
                                <th style={thStyle}>Calibration Agency</th>
                                <th style={thStyle}>Certificate No.</th>
                                <th style={thStyle}>Calibrated By</th>
                                <th style={thStyle}>Calibrated On</th>
                              </tr>
                            </thead>
                            <tbody>
                              {calibrationRecords.map((r) => (
                                <tr key={r.id}>
                                  <td style={tdStyle}>
                                    <button
                                      onClick={() => openExistingCalibrationRecord(r.id)}
                                      style={{ background: "none", border: "none", color: "#2563eb", textDecoration: "underline", cursor: "pointer", fontSize: 13, padding: 0 }}
                                    >
                                      {r.record_no}
                                    </button>
                                  </td>
                                  <td style={tdStyle}><StatusBadge status="Calibrated" /></td>
                                  <td style={tdStyle}>{r.calibration_agency || "-"}</td>
                                  <td style={tdStyle}>{r.certificate_no || "-"}</td>
                                  <td style={tdStyle}>{r.created_by || "-"}</td>
                                  <td style={tdStyle}>{r.created_at ? new Date(r.created_at).toLocaleString() : "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer intentionally left without buttons — Save now lives
                  in the header next to the info icon; Cancel was removed. */}
            </div>
          </>
        )}

        {/* ===================== DELETE CONFIRM ===================== */}
        {deleteTarget && (
          <div
            style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 1070, display: "flex", justifyContent: "center", alignItems: "center" }}
            onClick={closeDeleteConfirm}
          >
            <div
              style={{ background: "white", width: 380, maxWidth: "90vw", borderRadius: 12, padding: "28px 25px 25px", boxShadow: "0 10px 30px rgba(0,0,0,0.2)", position: "relative", textAlign: "center" }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeDeleteConfirm}
                style={{ position: "absolute", top: 14, right: 16, background: "transparent", border: "none", fontSize: 20, cursor: "pointer", color: "#334155", lineHeight: 1 }}
              >
                ✕
              </button>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              </div>
              <h4 style={{ margin: "0 0 8px", fontSize: 19, color: "#0f172a" }}>Delete Equipment</h4>
              <p style={{ margin: "0 0 22px", fontSize: 14, color: "#64748b" }}>
                Delete equipment "{deleteTarget.equipment_name}"? This cannot be undone.
              </p>
              <button
              
                onClick={confirmDeleteEquipment}
                style={{ width: "100%", background: "#ef4444", color: "white", border: "none", padding: 12, borderRadius: 8, fontWeight: 600, fontSize: 15, cursor: "pointer", marginBottom: 12 }}
              >
                Yes, Delete Equipment
              </button>
              <button
                onClick={closeDeleteConfirm}
                style={{ width: "100%", background: "transparent", border: "none", color: "#334155", fontSize: 14, fontWeight: 500, cursor: "pointer", padding: 6 }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LabEquipment;